import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchAdminServicesListJson,
  fetchAdminStaffListJson,
  fetchAdminBookingListJson,
  ADMIN_LIST_PAGE_SIZE,
  ADMIN_BOOKINGS_MAX_PAGE_SIZE,
} from '@/lib/queryClient';
import {
  isPlaceholderEntityId,
  normalizeAdminServicesListPayload,
  normalizeAdminStaffListPayload,
  normalizeBookingsListPayload,
} from '@/lib/adminApi';
import type { BookingWithDetails, Service, Staff } from '@shared/schema';

/**
 * Chains `fetchNextPage` until the full catalog is loaded.
 * Must only run when this observer has `enabled: true`; otherwise every mounted consumer
 * (e.g. calendar + closed drawer + closed modal) would each fire `fetchNextPage` in the same
 * tick before `isFetchingNextPage` flips — duplicating GETs (and CORS preflights) per page.
 */
function useAutoFetchAllPages(
  enabled: boolean,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => Promise<unknown>
) {
  useEffect(() => {
    if (!enabled) return;
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);
}

/**
 * Loads all pages of GET `/api/admin/services` (PagedListResponse) and merges rows.
 * Use wherever the UI needs the full offerings catalog (dropdowns, name resolution).
 */
export function useAdminServicesCatalog(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const q = useInfiniteQuery({
    queryKey: ['/api/admin/services', 'catalog', ADMIN_LIST_PAGE_SIZE] as const,
    queryFn: ({ pageParam }) =>
      fetchAdminServicesListJson(pageParam as number, ADMIN_LIST_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPageRaw) => {
      const p = normalizeAdminServicesListPayload(lastPageRaw);
      const m = p.metadata;
      if (!m?.page || !m?.totalPages) return undefined;
      return m.page < m.totalPages ? m.page + 1 : undefined;
    },
    enabled,
  });

  useAutoFetchAllPages(enabled, q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage);

  /**
   * De-dupe by id so a mis-paged API cannot repeat page 1 rows under later "pages".
   * Placeholder ids (`0`/empty) may represent several distinct rows; never collapse those.
   */
  const services = useMemo(() => {
    const pages = q.data?.pages;
    if (!pages?.length) return [];
    const seen = new Set<string>();
    const out: Service[] = [];
    for (const raw of pages) {
      for (const s of normalizeAdminServicesListPayload(raw).services) {
        const id = String(s.id);
        if (!isPlaceholderEntityId(id)) {
          if (seen.has(id)) continue;
          seen.add(id);
        }
        out.push(s);
      }
    }
    return out;
  }, [q.data?.pages]);

  const isLoading = q.isPending || Boolean(q.hasNextPage && (q.isFetchingNextPage || q.isFetching));

  return { services, isLoading, ...q };
}

/**
 * Loads all pages of GET `/api/admin/staff` (PagedListResponse) and merges rows.
 */
export function useAdminStaffCatalog(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const q = useInfiniteQuery({
    queryKey: ['/api/admin/staff', 'catalog', ADMIN_LIST_PAGE_SIZE] as const,
    queryFn: ({ pageParam }) => fetchAdminStaffListJson(pageParam as number, ADMIN_LIST_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPageRaw) => {
      const p = normalizeAdminStaffListPayload(lastPageRaw);
      const m = p.metadata;
      if (!m?.page || !m?.totalPages) return undefined;
      return m.page < m.totalPages ? m.page + 1 : undefined;
    },
    enabled,
  });

  useAutoFetchAllPages(enabled, q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage);

  /**
   * De-dupe by id so a mis-paged API cannot repeat page 1 rows under later "pages".
   * Placeholder ids (`0`/empty) may represent several distinct rows; never collapse those.
   */
  const staff = useMemo(() => {
    const pages = q.data?.pages;
    if (!pages?.length) return [];
    const seen = new Set<string>();
    const out: Staff[] = [];
    for (const raw of pages) {
      for (const s of normalizeAdminStaffListPayload(raw).staff) {
        const id = String(s.id);
        if (!isPlaceholderEntityId(id)) {
          if (seen.has(id)) continue;
          seen.add(id);
        }
        out.push(s);
      }
    }
    return out;
  }, [q.data?.pages]);

  const isLoading = q.isPending || Boolean(q.hasNextPage && (q.isFetchingNextPage || q.isFetching));

  return { staff, isLoading, ...q };
}

/**
 * Loads all pages of GET `/api/admin/bookings` and merges rows so client-side filters apply to the full set.
 */
export function useAdminBookingsCatalog(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const q = useInfiniteQuery({
    queryKey: ['/api/admin/bookings', 'catalog', ADMIN_BOOKINGS_MAX_PAGE_SIZE] as const,
    queryFn: ({ pageParam }) =>
      fetchAdminBookingListJson(pageParam as number, ADMIN_BOOKINGS_MAX_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPageRaw) => {
      const p = normalizeBookingsListPayload(lastPageRaw);
      const m = p.metadata;
      if (!m?.page || !m?.totalPages) return undefined;
      return m.page < m.totalPages ? m.page + 1 : undefined;
    },
    enabled,
  });

  useAutoFetchAllPages(enabled, q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage);

  /** De-dupe by id so overlapping pages (e.g. pagination drift) cannot repeat the same booking. */
  const bookings = useMemo(() => {
    const pages = q.data?.pages;
    if (!pages?.length) return [];
    const seen = new Set<string>();
    const out: BookingWithDetails[] = [];
    for (const raw of pages) {
      for (const b of normalizeBookingsListPayload(raw).bookings) {
        const id = String(b.id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(b);
      }
    }
    return out;
  }, [q.data?.pages]);

  const isLoading = q.isPending || Boolean(q.hasNextPage && (q.isFetchingNextPage || q.isFetching));

  return { bookings, isLoading, ...q };
}
